import { AfterContentInit, Component, EventEmitter, Input, Output } from '@angular/core'
import { MatDialog } from '@angular/material/dialog'
import { ActivatedRoute } from '@angular/router'
import { ListingHotelGQL, ListingHotelQuery, ListingQuery } from '../../../../../../graphql'
import { ListingDocumentsComponent } from '../shared/listing-documents/listing-documents'
import { ImageForSlider, UnsubscribeOnDestroy } from '../../../../../utility'
import { GalleryComponent } from '../../../../shared-member/organism/gallery/gallery'
import { SelectedTravel } from '../listing-travel-dates/listing-travel-dates'
import { TravelDates } from '../shared/travel-dates'
import { dialogWidth } from '../../../../../constant'

export interface HotelSuiteThumbnailData {
    image: string | undefined | null,
    name: string,
    id: string,
    maximumOccupancy?: number
}

@Component({
    selector: 'app-listing-hotel',
    templateUrl: 'listing-hotel.html',
    styleUrls: ['listing-hotel.scss']
})
export class ListingHotelComponent extends UnsubscribeOnDestroy implements AfterContentInit {

    @Input() listing: ListingQuery['listing']
    @Input() travelDates: TravelDates

    hotel: ListingHotelQuery['hotelListing']
    photos: ImageForSlider[]
    showFeatures: boolean
    occupancyRange: string | number
    bedroomsRange: string | number
    hotelSuitesTabs: {
        label: string,
        html?: string,
        tabContentIndex?: number,
        tabId?: string
    }[] = [
        { label: 'Overview' }
    ]
    selectedHotelSuite: ListingHotelQuery['hotelListing']['suites'][0] | undefined
    selectedHotelSuiteName: string
    showSuiteDescription = false
    hotelSuiteThumbnailData: HotelSuiteThumbnailData[] = []
    activeHotelTabIndex = 1
    showOverviewTab = false
    hasDocuments = false

    @Output() onReserveRoom = new EventEmitter<SelectedTravel>()
    @Output() onSuiteSelect = new EventEmitter()
    @Output() onSuiteThumbnailData = new EventEmitter()

    // Constructor
    constructor(private _listingHotelGQL: ListingHotelGQL,
                private _dialog: MatDialog,
                private _activatedRoute: ActivatedRoute) {
        super()

        this.showOverviewTab = this._activatedRoute.snapshot.queryParams['overview']
    }

    // Public
    ngAfterContentInit() {
        // https://stackoverflow.com/a/49385215/15092539
        this.photos = this.listing.summary.photos
            .sort((one, other) => {
                return Number(other.useAsCover) - Number(one.useAsCover)
            })
            .map(photo => ({
                url: photo.url
            }))
        
        this.hasDocuments = this.checkIfHasDocuments()
        this.showFeatures = !!this.listing.summary.highlightedFeatures.features?.length

        this.requestHotel()
    }

    getOccupancyRange() {
        const suiteOccupancies = this.hotel.suites.map((suite) => suite?.guestRestrictions?.maxOccupancy).sort((a, b) => a - b)
        const first = suiteOccupancies[0]
        const last = suiteOccupancies[suiteOccupancies.length - 1]
        return suiteOccupancies.length > 1 &&  first != last ? `${first} - ${last}` : first
    }

    getBedroomRange() {
        const suiteRooms = this.hotel.suites.map((suite) => suite.rooms.bedrooms.length).sort((a, b) => a - b)
        const first = suiteRooms[0]
        const last = suiteRooms[suiteRooms.length - 1]
        return suiteRooms.length > 1 &&  first != last ? `${first} - ${last}` : first
    }

    setHotelSuiteTabs() {
        // In absence of suite name create names based on numerical value
        this.hotel.suites.forEach((suite, index) => {
            const label = suite?.suiteName?.length ? suite?.suiteName : `Suite ${index + 1}`

            this.hotelSuitesTabs.push({
                label,
                html: `
                    <div>${label}</div>
                    <div class="_text-sm _leading-none _mt-1 _text-primary-1-tint-50">
                        ${suite?.guestRestrictions.maxOccupancy} Guests
                    </div>`,
                tabContentIndex: 2,
                tabId: suite.hotelSuiteId
            })
        })
    }

    setActiveSuite(index: number) {
        this.showSuiteDescription = false
        if ((index - 2) >= 0) {
            this.selectedHotelSuite = this.hotel.suites[index - 2]
            this.onSuiteSelect.emit(this.selectedHotelSuite)
            // As there are no suite names in the data use the tab label for now
            this.selectedHotelSuiteName = this.hotelSuitesTabs[index - 1].label
            this.showSuiteDescription = true
        } else {
            this.onSuiteSelect.emit(undefined)
        }
    }

    viewPhotos(photos) {
        this._dialog.open(GalleryComponent, {
            data: {
                subjectId: this.listing?.listingId,
                photos
            },
            panelClass: 'gallery-dialog',
            maxWidth: '100vw',
            maxHeight: '100vh',
            height: '100%',
            width: '100%',
        })
    }

    viewDocuments() {
        this._dialog.open(ListingDocumentsComponent, {
            data: {
                documents: this.listing?.asset?.documentLibrary?.categories
            },
            width: dialogWidth.md,
            height: '70%',
            maxHeight: '100vh'
        })
    }

    checkIfHasDocuments() {
        const categories = this.listing?.asset?.documentLibrary?.categories
        let hasDocs = false
        if (categories && categories.length > 1) {
            for (const category of categories) {
                if (category?.documents?.length) {
                    hasDocs = true
                    break;
                }
            }
        } 
        return hasDocs
    }

    getHotelSuiteThumbnailData() {
        this.hotel.suites.map(suite => {
            if (suite.suiteName && suite.hotelSuiteId) {
                this.hotelSuiteThumbnailData.push({
                    image: suite.imagery?.photos?.length ? suite.imagery?.photos[0]?.url : null,
                    name: suite.suiteName,
                    id: suite.hotelSuiteId,
                    maximumOccupancy: suite?.guestRestrictions?.maxOccupancy
                })
            }
        })

        this.onSuiteThumbnailData.emit(this.hotelSuiteThumbnailData)
    }

    // Private
    private requestHotel() {
        this._subscriptions.push(this._listingHotelGQL
            .fetch({ hotelId: this.listing.asset.assetId }, { fetchPolicy: 'no-cache' })
            .subscribe(result => {
                if (!result.loading) {
                    this.hotel = result.data.hotelListing
                    console.log('hotel ', this.hotel)
                    this.setHotelSuiteTabs()
                    this.getHotelSuiteThumbnailData()
                    this.occupancyRange = this.getOccupancyRange()
                    this.bedroomsRange = this.getBedroomRange()

                    if (this.showOverviewTab) {
                        this.selectedHotelSuite = this.hotel?.suites[0]
                        this.activeHotelTabIndex = 0
                    } 
                    else {
                        this.selectedHotelSuite = this.hotel?.suites.find(suite => suite.hotelSuiteId === this.listing.listingId)
                        this.activeHotelTabIndex = this.hotel?.suites.findIndex(suite => suite.hotelSuiteId === this.listing.listingId) + 2;
                    }
                }
            }))
    }
}
